import React, { useContext } from 'react';
import _ from 'lodash';
import moment from 'moment';
import { numberWithCommas } from 'helper';
import PropTypes from 'prop-types';
import StatusIcon from 'modules/common/components/StatusIcon';
import CmsContext from 'context/CmsContext';

export const RowContent = ({ label, value }) => {
  return (
    <div className="row d-flex">
      <div className="col-6 py-2 px-1">
        <label className="font-weight-bold mb-0 mr-2">{label}</label>
      </div>
      <div className="col-6 py-2 px-1">
        <span className="transaction-text p-0">{value}</span>
      </div>
    </div>
  );
};

RowContent.defaultProps = {
  value: '-',
};

RowContent.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
};

function TransactionInformation({ transactionInfo, taxpayer }) {
  const cmsContext = useContext(CmsContext);

  const CMS_VARIABLES = cmsContext.findByType('VARIABLES');

  const serialNo =
    _.get(
      transactionInfo,
      'included.module.attributes.serial_number_complete'
    ) || '-';

  const permitNo =
    _.get(transactionInfo, 'included.module.attributes.permit_number') || '-';

  const certificateNo =
    _.get(transactionInfo, 'included.module.attributes.certificate_number') ||
    '-';

  return (
    <div className="alternate-bg-wrapper p-3 mx-3">
      <RowContent
        label="Valid ID to Present"
        value={_.get(transactionInfo, 'attributes.valid_id_to_present')}
      />
      <RowContent
        label={`Is Resident of ${_.get(CMS_VARIABLES, 'LGU_NAME')}`}
        value={
          _.get(transactionInfo, 'included.taxPayer.attributes.is_resident')
            ? 'YES'
            : 'NO'
        }
      />
      <RowContent
        label="Payment Status"
        value={
          // eslint-disable-next-line react/jsx-wrap-multilines
          _.get(transactionInfo, 'attributes.status') ? (
            <StatusIcon value={_.get(transactionInfo, 'attributes.status')} />
          ) : (
            _.get(transactionInfo, 'attributes.status')
          )
        }
      />
      <RowContent
        label="Permit Type"
        value={_.get(transactionInfo, 'attributes.permit_type')}
      />
      <RowContent
        label="Transaction Type"
        value={_.get(transactionInfo, 'attributes.transaction_type')}
      />
      <RowContent
        label="Status"
        value={
          // eslint-disable-next-line react/jsx-wrap-multilines
          <StatusIcon
            value={_.get(transactionInfo, 'attributes.transaction_status')}
          />
        }
      />
      {['RENEWAL', 'TAX CLEARANCE'].indexOf(
        _.get(transactionInfo, 'attributes.transaction_type')
      ) > -1 && (
        <RowContent
          label="Print Status"
          value={
            // eslint-disable-next-line react/jsx-wrap-multilines
            _.get(transactionInfo, 'included.module.attributes.status') ? (
              <StatusIcon
                value={_.get(
                  transactionInfo,
                  'included.module.attributes.status'
                )}
              />
            ) : (
              '-'
            )
          }
        />
      )}
      <RowContent
        label="Transmittal No."
        value={
          _.get(transactionInfo, 'attributes.bpls_batch_number') ||
          _.get(transactionInfo, 'included.module.attributes.transmittal_no') ||
          '-'
        }
      />
      <RowContent
        label="Transaction ID"
        value={_.get(transactionInfo, 'attributes.txnid')}
      />
      <RowContent
        label="Reference Number Generated At"
        value={
          _.get(transactionInfo, 'attributes.pending_at')
            ? moment(_.get(transactionInfo, 'attributes.pending_at')).format(
                'MM/DD/YYYY'
              )
            : '-'
        }
      />
      <RowContent
        label="Reference Number"
        value={_.get(transactionInfo, 'attributes.refno') ?? '-'}
      />
      <RowContent
        label="Payment Amount"
        value={`PHP ${numberWithCommas(
          _.get(transactionInfo, 'attributes.amount', 0)
        )}`}
      />
      <RowContent
        label="Is Posted"
        value={
          _.get(transactionInfo, 'included.tax_payer.attributes.is_posted')
            ? 'YES'
            : 'NO'
        }
      />
      <div>
        <hr className="cit-hr w-100" />
      </div>
      <RowContent label="Permit Number" value={permitNo} />
      <RowContent label="Certificate Number" value={certificateNo} />
      <RowContent
        label="Appointment Date"
        value={
          _.get(taxpayer, 'appointment_date', '')
            ? `${moment(
                _.get(
                  transactionInfo,
                  'included.module.attributes.appointment_date'
                )
              ).format('MM/DD/YYYY')} ${_.get(taxpayer, 'appointment_period')}`
            : '-'
        }
      />
      <RowContent label="Serial Number" value={serialNo} />
      <div>
        <hr className="cit-hr w-100" />
      </div>
      {_.get(transactionInfo, 'attributes.printed_by') && (
        <>
          <RowContent
            label="Printed By"
            value={_.get(transactionInfo, 'attributes.printed_by_full_name')}
          />
          <RowContent
            label="Printed Date"
            value={
              _.get(transactionInfo, 'attributes.printed_at')
                ? moment(
                    _.get(transactionInfo, 'attributes.printed_at')
                  ).format('MM/DD/YYYY')
                : `-`
            }
          />
        </>
      )}
      {_.get(transactionInfo, 'attributes.received_by') && (
        <>
          <RowContent
            label="Issued By"
            value={_.get(transactionInfo, 'attributes.received_by_full_name')}
          />
          <RowContent
            label="Issued Date"
            value={
              _.get(transactionInfo, 'attributes.received_at')
                ? moment(
                    _.get(transactionInfo, 'attributes.received_at')
                  ).format('MM/DD/YYYY')
                : `-`
            }
          />
        </>
      )}
      {_.get(transactionInfo, 'attributes.reject_by') && (
        <>
          <RowContent
            label="Rejected By"
            value={_.get(transactionInfo, 'attributes.reject_by_full_name')}
          />
          <RowContent
            label="Rejected Date"
            value={
              _.get(transactionInfo, 'attributes.reject_at')
                ? moment(_.get(transactionInfo, 'attributes.reject_at')).format(
                    'MM/DD/YYYY'
                  )
                : `-`
            }
          />
        </>
      )}
      {_.get(transactionInfo, 'attributes.approve_by') && (
        <>
          <RowContent
            label="Approved By"
            value={_.get(transactionInfo, 'attributes.approve_by_full_name')}
          />
          <RowContent
            label="Approved Date"
            value={
              _.get(transactionInfo, 'attributes.approve_at')
                ? moment(
                    _.get(transactionInfo, 'attributes.approve_at')
                  ).format('MM/DD/YYYY')
                : `-`
            }
          />
          <RowContent
            label="Delivery Option"
            value={
              _.get(transactionInfo, 'attributes.delivery_location') || 0
                ? 'FOR DELIVERY'
                : 'FOR PICKUP'
            }
          />
        </>
      )}
      <RowContent
        label="Remarks"
        value={_.get(transactionInfo, 'attributes.remarks') ?? '-'}
      />
      {_.get(transactionInfo, 'included.module.attributes.reject_remarks') && (
        <RowContent
          label="Reject Remarks"
          value={_.get(
            transactionInfo,
            'included.module.attributes.reject_remarks'
          )}
        />
      )}
    </div>
  );
}

TransactionInformation.defaultProps = {
  taxpayer: {},
};

TransactionInformation.propTypes = {
  transactionInfo: PropTypes.instanceOf(Object).isRequired,
  taxpayer: PropTypes.instanceOf(Object),
};

export default TransactionInformation;
